import React, { useState } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiSearch, FiFilter, FiCalendar, FiUser } from 'react-icons/fi';

const NewsSection: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [newsArticles, setNewsArticles] = useState<any[]>([]);
  const [addForm, setAddForm] = useState({
    title: '',
    title_hindi: '',
    description: '',
    description_hindi: '',
    thumbnail: '',
    category: '',
    likes: '',
    date: '',
  });
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<any>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Fetch news from API
  React.useEffect(() => {
    fetchNews();
  }, []);

  // Helper function to convert image path to full URL
  const getImageUrl = (imagePath: string): string => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    const baseUrl = 'https://mandilive.in';
    let cleanPath = imagePath.replace(/^[\\\/]+/, '');
    cleanPath = cleanPath.replace(/\\/g, '/');
    if (!cleanPath.startsWith('uploads/')) cleanPath = `uploads/${cleanPath}`;
    return `${baseUrl}/${cleanPath}`;
  };

  const fetchNews = async () => {
    setLoading(true);
    try {
      const res = await fetch('https://mandilive.in/api/news');
      const data = await res.json();
      // Convert thumbnail/image path to full URL for each article
      const processed = (data.data || []).map((article: any) => ({
        ...article,
        thumbnail: getImageUrl(article.thumbnail),
      }));
      setNewsArticles(processed);
    } catch (err) {
      setNewsArticles([]);
    }
    setLoading(false);
  };

  // Handle add form change
  const handleAddFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setAddForm({ ...addForm, [e.target.name]: e.target.value });
  };

  // Handle add news
  const handleAddNews = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);
    try {
      const res = await fetch('https://mandilive.in/api/news', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      if (res.ok) {
        setShowAddForm(false);
        setAddForm({ title: '', title_hindi: '', description: '', description_hindi: '', thumbnail: '', category: '', likes: '', date: '' });
        fetchNews();
      } else {
        alert('Failed to add news');
      }
    } catch (err) {
      alert('Error adding news');
    } finally {
      setUploading(false);
    }
  };

  // Categories for filter and add form
  const categories = ['Government Schemes', 'Weather', 'Technology', 'Farming Techniques', 'Market Analysis'];

  const filteredArticles = newsArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || article.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || article.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">News Section</h1>
          <p className="text-gray-600 mt-1">Manage your news articles and content</p>
        </div>
        <button
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          onClick={() => setShowAddForm(v => !v)}
        >
          <FiPlus className="mr-2 h-4 w-4" />
          {showAddForm ? 'Close' : 'Add New Article'}
        </button>
      </div>

      {/* Filters and Search */}
      

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiEye className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Articles</p>
              <p className="text-xl font-semibold text-gray-900">{newsArticles.length}</p>
            </div>
          </div>
        </div>
      
        </div>
     

      
      {showAddForm && (
        <form
          className="bg-white p-6 rounded-lg shadow mb-8 grid grid-cols-1 md:grid-cols-2 gap-4"
          onSubmit={async e => {
            e.preventDefault();
            // Validate
            const newErrors: any = {};
            if (!addForm.title.trim()) newErrors.title = 'Title is required';
            if (!addForm.title_hindi.trim()) newErrors.title_hindi = 'Title (Hindi) is required';
            if (!addForm.description.trim()) newErrors.description = 'Description is required';
            if (!addForm.description_hindi.trim()) newErrors.description_hindi = 'Description (Hindi) is required';
            if (!addForm.category.trim()) newErrors.category = 'Category is required';
            if (!addForm.date.trim()) newErrors.date = 'Date is required';
            if (!addForm.likes || isNaN(Number(addForm.likes))) newErrors.likes = 'Likes must be a number';
            if (!thumbnailFile) newErrors.thumbnail = 'Thumbnail image is required';
            setErrors(newErrors);
            if (Object.keys(newErrors).length > 0) return;
            setUploading(true);
            try {
              const formData = new FormData();
              formData.append('title', addForm.title);
              formData.append('title_hindi', addForm.title_hindi);
              formData.append('description', addForm.description);
              formData.append('description_hindi', addForm.description_hindi);
              formData.append('category', addForm.category);
              formData.append('date', addForm.date);
              formData.append('likes', addForm.likes);
              formData.append('thumbnail', thumbnailFile!);
              const res = await fetch('https://mandilive.in/api/news', {
                method: 'POST',
                body: formData,
              });
              if (res.ok) {
                setShowAddForm(false);
                setAddForm({ title: '', title_hindi: '', description: '', description_hindi: '', thumbnail: '', category: '', likes: '', date: '' });
                setThumbnailFile(null);
                setErrors({});
                fetchNews();
              } else {
                alert('Failed to add article');
              }
            } catch (err) {
              alert('Error adding article');
            } finally {
              setUploading(false);
            }
          }}
        >
          <div>
            <label className="block font-medium">Title</label>
            <input type="text" className="w-full border px-3 py-2 rounded" name="title" value={addForm.title} onChange={e => setAddForm(f => ({ ...f, title: e.target.value }))} />
            {errors.title && <div className="text-red-600 text-xs">{errors.title}</div>}
          </div>
          <div>
            <label className="block font-medium">Title (Hindi)</label>
            <input type="text" className="w-full border px-3 py-2 rounded" name="title_hindi" value={addForm.title_hindi} onChange={e => setAddForm(f => ({ ...f, title_hindi: e.target.value }))} />
            {errors.title_hindi && <div className="text-red-600 text-xs">{errors.title_hindi}</div>}
          </div>
          <div>
            <label className="block font-medium">Description</label>
            <textarea className="w-full border px-3 py-2 rounded" name="description" value={addForm.description} onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))} />
            {errors.description && <div className="text-red-600 text-xs">{errors.description}</div>}
          </div>
          <div>
            <label className="block font-medium">Description (Hindi)</label>
            <textarea className="w-full border px-3 py-2 rounded" name="description_hindi" value={addForm.description_hindi} onChange={e => setAddForm(f => ({ ...f, description_hindi: e.target.value }))} />
            {errors.description_hindi && <div className="text-red-600 text-xs">{errors.description_hindi}</div>}
          </div>
          <div>
            <label className="block font-medium">Category</label>
            <input type="text" className="w-full border px-3 py-2 rounded" name="category" value={addForm.category} onChange={e => setAddForm(f => ({ ...f, category: e.target.value }))} />
            {errors.category && <div className="text-red-600 text-xs">{errors.category}</div>}
          </div>
          <div>
            <label className="block font-medium">Likes</label>
            <input type="number" className="w-full border px-3 py-2 rounded" name="likes" value={addForm.likes} onChange={e => setAddForm(f => ({ ...f, likes: e.target.value }))} />
            {errors.likes && <div className="text-red-600 text-xs">{errors.likes}</div>}
          </div>
          <div>
            <label className="block font-medium">Date</label>
            <input type="date" className="w-full border px-3 py-2 rounded" name="date" value={addForm.date} onChange={e => setAddForm(f => ({ ...f, date: e.target.value }))} />
            {addForm.date && (
              <div className="mt-1 text-green-700 text-sm font-semibold">
                {new Date(addForm.date).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            )}
            {errors.date && <div className="text-red-600 text-xs">{errors.date}</div>}
          </div>
          <div>
            <label className="block font-medium">Thumbnail Image</label>
            <input type="file" accept="image/*" className="w-full" onChange={e => setThumbnailFile(e.target.files ? e.target.files[0] : null)} />
            {errors.thumbnail && <div className="text-red-600 text-xs">{errors.thumbnail}</div>}
            {thumbnailFile && <div className="mt-2 text-xs text-gray-500">Selected: {thumbnailFile.name}</div>}
          </div>
          <div className="md:col-span-2">
            <button disabled={uploading} type="submit" className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
              {uploading ? 'Adding...' : 'Add Article'}
            </button>
          </div>
        </form>
      )}

      {/* Articles List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Articles</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredArticles.map((article) => (
            <div key={article.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-start space-x-4">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  className="w-24 h-16 object-cover rounded-lg flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-lg font-medium text-gray-900 mb-2">{article.title}</h4>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                      
                        <div className="flex items-center">
                         
                          {typeof article.likes === 'number' && !isNaN(article.likes)
                            ? article.likes.toLocaleString()
                            : (article.likes ? String(article.likes) : '0')
                          } likes
                        </div>
                       
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {article.category}
                      </span>
                      <button
                        className="p-2 text-gray-400 hover:text-red-600"
                        onClick={async () => {
                          if (!window.confirm('क्या आप वाकई इस आर्टिकल को हटाना चाहते हैं? (Are you sure you want to delete this article?)')) return;
                          try {
                            const res = await fetch(`https://mandilive.in/api/news/${article.id}`, { method: 'DELETE' });
                            if (res.ok) {
                              fetchNews();
                            } else {
                              alert('Failed to delete article');
                            }
                          } catch (err) {
                            alert('Error deleting article');
                          }
                        }}
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Empty State */}
      {filteredArticles.length === 0 && (
        <div className="text-center py-12">
          <FiEye className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No articles found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterCategory !== 'all' || filterStatus !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating your first article.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default NewsSection;
